import React, { useState, useMemo } from 'react'
import { Button } from './Button'
import { ChevronUp, ChevronDown, Search, Filter, Download, BarChart3, TrendingUp, TrendingDown } from 'lucide-react'

interface DataColumn {
  key: string
  label: string
  type: 'string' | 'number' | 'date' | 'currency'
  sortable?: boolean
}

interface DataTableProps {
  data: Record<string, any>[]
  columns: DataColumn[]
  onAnalyze?: (data: Record<string, any>[]) => void
  onExport?: (data: Record<string, any>[]) => void
  showInsights?: boolean
}

interface SortConfig {
  key: string
  direction: 'asc' | 'desc'
}

export function DataTable({ data, columns, onAnalyze, onExport, showInsights = false }: DataTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null)
  const [filterText, setFilterText] = useState('')
  const [selectedColumns, setSelectedColumns] = useState<string[]>(columns.map(c => c.key))

  const filteredAndSortedData = useMemo(() => {
    let processedData = [...data]

    // Apply text filter
    if (filterText) {
      processedData = processedData.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(filterText.toLowerCase())
        )
      )
    }

    // Apply sorting
    if (sortConfig) {
      processedData.sort((a, b) => {
        const aValue = a[sortConfig.key]
        const bValue = b[sortConfig.key]
        
        if (aValue === bValue) return 0
        
        const result = aValue < bValue ? -1 : 1
        return sortConfig.direction === 'asc' ? result : -result
      })
    }

    return processedData
  }, [data, filterText, sortConfig])

  const handleSort = (key: string) => {
    setSortConfig(current => {
      if (current?.key === key) {
        return current.direction === 'asc'
          ? { key, direction: 'desc' }
          : null
      }
      return { key, direction: 'asc' }
    })
  }

  const formatValue = (value: any, type: string) => {
    if (value === null || value === undefined) return '-'
    
    switch (type) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD'
        }).format(Number(value))
      case 'number':
        return new Intl.NumberFormat('en-US').format(Number(value))
      case 'date':
        return new Date(value).toLocaleDateString()
      default:
        return String(value)
    }
  }

  const getColumnInsights = (column: DataColumn) => {
    if (column.type !== 'number' && column.type !== 'currency') return null
    
    const values = data.map(row => Number(row[column.key])).filter(v => !isNaN(v))
    if (values.length === 0) return null

    const total = values.reduce((sum, val) => sum + val, 0)
    const avg = total / values.length
    const min = Math.min(...values)
    const max = Math.max(...values)

    return { total, avg, min, max, count: values.length }
  }

  const visibleColumns = columns.filter(col => selectedColumns.includes(col.key))

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search data..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="pl-9 pr-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-primary w-64"
            />
          </div>
          
          <Button variant="outline" size="sm" className="gap-2">
            <Filter className="w-4 h-4" />
            Filter
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {onAnalyze && (
            <Button onClick={() => onAnalyze(filteredAndSortedData)} className="gap-2">
              <BarChart3 className="w-4 h-4" />
              Analyze
            </Button>
          )}
          
          {onExport && (
            <Button
              onClick={() => onExport(filteredAndSortedData)}
              variant="outline"
              className="gap-2"
            >
              <Download className="w-4 h-4" />
              Export
            </Button>
          )}
        </div>
      </div>

      {/* Data Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-muted-foreground">Total Rows</div>
          <div className="text-xl font-semibold">{filteredAndSortedData.length}</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-muted-foreground">Columns</div>
          <div className="text-xl font-semibold">{visibleColumns.length}</div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-muted-foreground">Filtered</div>
          <div className="text-xl font-semibold">
            {data.length !== filteredAndSortedData.length ? 'Yes' : 'No'}
          </div>
        </div>
        <div className="bg-muted/50 rounded-lg p-3">
          <div className="text-muted-foreground">Status</div>
          <div className="text-xl font-semibold text-green-600">Ready</div>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                {visibleColumns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-left text-sm font-medium ${
                      column.sortable !== false ? 'cursor-pointer hover:bg-muted' : ''
                    }`}
                    onClick={() => column.sortable !== false && handleSort(column.key)}
                  >
                    <div className="flex items-center gap-2">
                      <span>{column.label}</span>
                      {column.sortable !== false && (
                        <div className="flex flex-col">
                          <ChevronUp
                            className={`w-3 h-3 ${
                              sortConfig?.key === column.key && sortConfig.direction === 'asc'
                                ? 'text-primary'
                                : 'text-muted-foreground/50'
                            }`}
                          />
                          <ChevronDown
                            className={`w-3 h-3 -mt-1 ${
                              sortConfig?.key === column.key && sortConfig.direction === 'desc'
                                ? 'text-primary'
                                : 'text-muted-foreground/50'
                            }`}
                          />
                        </div>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.map((row, index) => (
                <tr
                  key={index}
                  className={`border-t hover:bg-muted/30 ${
                    index % 2 === 0 ? 'bg-background' : 'bg-muted/10'
                  }`}
                >
                  {visibleColumns.map((column) => (
                    <td key={column.key} className="px-4 py-3 text-sm">
                      {formatValue(row[column.key], column.type)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Column Insights */}
      {showInsights && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visibleColumns
            .filter(col => col.type === 'number' || col.type === 'currency')
            .map((column) => {
              const insights = getColumnInsights(column)
              if (!insights) return null

              return (
                <div key={column.key} className="border rounded-lg p-4">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-green-600" />
                    {column.label} Insights
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-medium">
                        {formatValue(insights.total, column.type)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Average:</span>
                      <span className="font-medium">
                        {formatValue(insights.avg, column.type)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Min:</span>
                      <span className="font-medium">
                        {formatValue(insights.min, column.type)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Max:</span>
                      <span className="font-medium">
                        {formatValue(insights.max, column.type)}
                      </span>
                    </div>
                  </div>
                </div>
              )
            })}
        </div>
      )}

      {filteredAndSortedData.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium mb-2">No Data Found</p>
          <p className="text-sm">
            {filterText ? 'Try adjusting your search criteria' : 'Upload data to get started'}
          </p>
        </div>
      )}
    </div>
  )
}